/**
 * Firebase Auth, on its own so the sign-in page loads nothing else.
 *
 * Auth is a global Firebase service; by policy no PII beyond email lives in
 * it. Profiles are stored in Firestore africa-south1 under users/{uid}.
 *
 * initializeAuth rather than getAuth. getAuth installs the popup and
 * redirect resolver, and on a phone that resolver loads Google's gapi script
 * and an auth iframe on every page view before Auth will say who is signed
 * in: 41 KB and about 320 ms of a mid-range phone's main thread when the CSP
 * lets it through, and a CSP violation the SDK waits out when it does not.
 * Nothing here signs in with a popup or a redirect. If Google sign-in is
 * added, pass browserPopupRedirectResolver to that one call. The persistence
 * list is getAuth's own, so a session saved before this change is still
 * found.
 */
import { useEffect, useState } from 'react';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  initializeAuth,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { app, emulatorPortShift, useEmulators } from './firebaseApp';
import { registerServiceWorkerAfterLoad } from './sw';

// Every route that reaches the workspace (sign-in, the root redirect, the
// shell) imports this module first, so the service worker's backstop timer
// is started here. See sw.ts for why it no longer registers on `load`.
registerServiceWorkerAfterLoad();

export const auth = initializeAuth(app, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
});

if (useEmulators) {
  connectAuthEmulator(auth, `http://127.0.0.1:${9099 + emulatorPortShift}`, { disableWarnings: true });
}

export function useAuthUser(): { user: User | null; loading: boolean } {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [loading, setLoading] = useState(true);
  useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        setUser(u);
        setLoading(false);
      }),
    [],
  );
  return { user, loading };
}
